import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getSkillsList,
  getAgentsList,
  TransferSkill,
  TransferAgent,
} from '../services/cxone-sdk.service';
import { logger } from '../utils/logger';

export function useTransferData() {
  const { t } = useTranslation();
  const [skills, setSkills] = useState<TransferSkill[]>([]);
  const [agents, setAgents] = useState<TransferAgent[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      setIsLoadingData(true);
      setLoadError(null);
      try {
        const [skillList, agentList] = await Promise.all([getSkillsList(), getAgentsList()]);
        setSkills(skillList);
        setAgents(agentList);
      } catch (err) {
        logger.error('[TransferPanel] Failed to fetch data:', err);
        setLoadError(t('call:transfer.failedToLoad'));
      } finally {
        setIsLoadingData(false);
      }
    }

    void fetchData();
  }, [t]);

  const filteredSkills = useMemo(() => {
    if (!searchQuery.trim()) {
      return skills;
    }
    const query = searchQuery.toLowerCase();
    return skills.filter((skill) => skill.skillName.toLowerCase().includes(query));
  }, [skills, searchQuery]);

  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) {
      return agents;
    }
    const query = searchQuery.toLowerCase();
    return agents.filter(
      (agent) =>
        agent.agentName.toLowerCase().includes(query) ||
        agent.firstName.toLowerCase().includes(query) ||
        agent.lastName.toLowerCase().includes(query) ||
        (agent.teamName && agent.teamName.toLowerCase().includes(query))
    );
  }, [agents, searchQuery]);

  const clearSearch = () => setSearchQuery('');

  return {
    skills,
    agents,
    searchQuery,
    setSearchQuery,
    filteredSkills,
    filteredAgents,
    isLoadingData,
    loadError,
    clearSearch,
  };
}

export type { TransferSkill, TransferAgent };
